import React, { useRef } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  styled,
} from '@mui/material';
import Minus from 'assets/images/Minus.png';
import Plus from 'assets/images/Plus.png';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const CustomTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    height: '64px',
    borderRadius: '12px',
    fontSize: '24px',
    fontWeight: 700,
    backgroundColor: '#fff',
    // Default border
    '& fieldset': { borderColor: '#E0E0E0' },
    // Remove blue/highlight border on hover
    '&:hover fieldset': { borderColor: '#E0E0E0 !important' },
    // Remove blue/highlight border on focus
    '&.Mui-focused fieldset': {
      borderColor: '#E0E0E0 !important',
      borderWidth: '1px !important',
    },
  },
  '& .MuiInputBase-input': {
    textAlign: 'center',
    paddingLeft: '20px',
    outline: 'none',
  },
});

const OfferInput = ({
  totalAmount,
  setTotalAmount,
  increment,
  formattedTotalAmount,
  setFormattedTotalAmount,
  iconDis,
  setIconDis,
  setPlaceholder,
  placeholder,
  submitOfferHandler,
}) => {
  const inputRef = useRef(null);
  const isFocused = document.activeElement === inputRef.current;
  const handleIncrement = () => {
    const safeTotal = Number(totalAmount) || 0;
    const safeIncrement = Number(increment) || 0;
    const newVal = safeTotal + safeIncrement;
    updateStates(newVal);
  };

  const handleDecrement = () => {
    if (totalAmount <= increment) return;
    const safeTotal = Number(totalAmount) || 0;
    const safeIncrement = Number(increment) || 0;
    if (safeTotal > 0) {
      const newVal = Math.max(0, safeTotal - safeIncrement);
      updateStates(newVal);
    }
  };

  const updateStates = (num) => {
    setTotalAmount(num);
    // Setting to '' instead of '0' allows the placeholder to work and kills the jumping cursor
    setFormattedTotalAmount(num === 0 ? '' : num.toLocaleString());
    setIconDis(num.toString().length >= 9);
  };
  const handleChange = (e) => {
    const input = e.target;
    const rawInputValue = e.target.value;
    const cursorPosition = input.selectionStart;
    const inputType = e.nativeEvent?.inputType || ''; // helps with fast typing tracking

    // Extract current character before formatting happens
    const typedChar = rawInputValue[cursorPosition - 1];

    // Strip commas and keep only numbers
    let numericRaw = rawInputValue.replace(/,/g, '');
    if (!/^\d*$/.test(numericRaw)) return;

    // Trim max 9 digits
    numericRaw = numericRaw.slice(0, 9);

    // Format the number
    const formattedValue = numericRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Update states
    setTotalAmount(parseFloat(numericRaw || '0'));
    setFormattedTotalAmount(formattedValue);
    setIconDis(numericRaw.length >= 9);

    // Now calculate fixed cursor position based on number of digits typed before original cursor
    const digitsBeforeCursor = rawInputValue
      .slice(0, cursorPosition)
      .replace(/\D/g, '').length;

    let newCursorPos = 0;
    let digitCount = 0;
    while (
      digitCount < digitsBeforeCursor &&
      newCursorPos < formattedValue.length
    ) {
      if (/\d/.test(formattedValue[newCursorPos])) {
        digitCount++;
      }
      newCursorPos++;
    }

    // Special case: if user is typing fast and the character jumps after a comma
    // ensure the cursor is at right place if comma was added just before
    if (inputType === 'insertText' && formattedValue[newCursorPos] === ',') {
      newCursorPos += 1;
    }

    // Set final cursor position AFTER state updates
    requestAnimationFrame(() => {
      input.setSelectionRange(newCursorPos, newCursorPos);
    });
  };

  const handleFocus = (e) => {
    setPlaceholder('');
    // Force cursor to the end of any existing text (or start if empty)
    const len = e.target.value.length;
    e.target.setSelectionRange(len, len);
  };

  const handleBlur = (e) => {
    if (!e.target.value.trim() || totalAmount == 0) {
      setPlaceholder('0');
    }
  };

  const getDollarPosition = () => {
    // If formattedTotalAmount is empty or '0', treat charCount as 1 for the placeholder '0'
    const charCount =
      !formattedTotalAmount ||
      formattedTotalAmount === '' ||
      formattedTotalAmount === '0'
        ? 1
        : formattedTotalAmount.length;

    return `calc(50% - ${charCount * 5}px - 25px)`;
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <CustomTextField
        fullWidth
        value={
          formattedTotalAmount === '0' || totalAmount === 0
            ? ''
            : formattedTotalAmount
        }
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        inputRef={inputRef}
        autoComplete="off"
        placeholder={
          isFocused && (totalAmount === 0 || formattedTotalAmount === '0')
            ? ''
            : placeholder
        }
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            submitOfferHandler(); // <----- submit on Enter
          }
        }}
        inputProps={{
          style: {
            fontSize: 20,
            fontWeight: 400,
            fontFamily: 'poppins',
            color: '#666666',
            textAlign: 'center',
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton
                onClick={handleDecrement}
                sx={{
                  ml: 1,
                  cursor: totalAmount <= increment ? 'not-allowed' : 'pointer',
                  opacity: totalAmount <= increment ? 0.25 : 1,
                  pointerEvents:
                    totalAmount <= increment ? 'not-allowed' : 'auto',
                }}
              >
                <img src={Minus} alt="minus" style={{ width: 22 }} />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleIncrement}
                sx={{
                  mr: 1,
                  opacity: iconDis && 0.5,
                  pointerEvents: iconDis && 'none',
                  cursor: 'pointer',
                }}
              >
                <img src={Plus} alt="plus" style={{ width: 22 }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          left: getDollarPosition(),
          pointerEvents: 'none',
          color: '#666',
          display: 'flex',
          alignItems: 'center',
          transition: 'left 0.1s ease-out',
        }}
      >
        <AttachMoneyIcon fontSize="large" />
      </Box>
    </Box>
  );
};

export default OfferInput;
